import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  GridItem,
  Text,
  Spinner,
  Input,
  VStack,
  HStack,
  useToast,
  Flex
} from '@chakra-ui/react';
import api from '../api';

const ProductCardPrinter = () => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const toast = useToast();
  const observer = useRef();

  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/products/?page=${page}&search=${search}`);
        const results = response.data.results || [];
        setProducts((prev) => [...prev, ...results]);
        setHasMore(Boolean(response.data.next));
      } catch (error) {
        toast({ title: 'Помилка при завантаженні товарів', status: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [page, search]);

  const handleToggleProduct = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handlePrint = () => {
    const toPrint = products.filter((product) => selectedProducts.includes(product.id));
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <html>
      <head>
        <title>Картки товарів</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            margin: 0;
            padding: 20px;
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            align-items: flex-start;
          }
          .card {
            border: 2px solid #000;
            border-radius: 8px;
            width: 220px;
            height: 140px;
            margin: 10px;
            padding: 10px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            position: relative;
            page-break-inside: avoid;
          }
          .header {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            font-weight: bold;
          }
          .name {
            text-align: center;
            font-size: 15px;
            font-weight: bold;
            margin: 6px 0;
            overflow-wrap: break-word;
            white-space: normal;
            max-height: 48px;
            overflow: hidden;
          }
          .catalog {
            text-align: center;
            font-size: 11px;
            color: #333;
          }
          .price {
            text-align: center;
            font-size: 20px;
            font-weight: bold;
            margin-top: 4px;
          }
        </style>
      </head>
      <body>
    `);

    toPrint.forEach((product) => {
      printWindow.document.write(`
        <div class="card">
          <div class="header">
            <span>ID: ${product.id}</span>
            <span></span>
          </div>
          <div class="name">${product.name}</div>
          <div class="catalog">Арт: ${product.catalog_number || '—'}</div>
          <div class="price">Ціна: ${Number(product.price).toFixed(2)} грн</div>
        </div>
      `);
    });

    printWindow.document.write(`
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };


  const filteredProducts = Array.isArray(products)
    ? products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    : [];

  return (
    <Box p={6}>
      <Text fontSize="2xl" fontWeight="bold" mb={4}>Друк карток товарів</Text>

      <HStack mb={4}>
        <Input
          placeholder="Пошук товару..."
          value={search}
          onChange={(e) => {
            setProducts([]);
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <Button onClick={handlePrint} colorScheme="blue" isDisabled={selectedProducts.length === 0}>
          Друкувати обране
        </Button>
      </HStack>

      <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={4}>
        {filteredProducts.map((product, index) => {
          const isLast = index === filteredProducts.length - 1;
          return (
            <GridItem
              key={product.id}
              ref={isLast ? lastProductRef : null}
              borderWidth="2px"
              borderRadius="lg"
              p={3}
              boxShadow="md"
              bg="white"
            >
              <VStack align="stretch" h="100%" justify="space-between" spacing={3}>
                {/* Top row: ID and checkbox */}
                <Flex justify="space-between" align="flex-start">
                  <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                    Продукт ID: {product.id}
                  </Text>
                  <Checkbox
                    isChecked={selectedProducts.includes(product.id)}
                    onChange={() => handleToggleProduct(product.id)}
                    colorScheme="blue"
                  />
                </Flex>

                {/* Catalog Number */}
                <Text fontSize="xs" color="gray.600" noOfLines={1}>
                  Катал-номер: {product.catalog_number || '—'}
                </Text>

                {/* Product name */}
                <Box flex="1" minH="60px">
                  <Text
                    fontWeight="bold"
                    fontSize="md"
                    wordBreak="break-word"
                    whiteSpace="normal"
                    overflowWrap="break-word"
                  >
                    {product.name}
                  </Text>
                </Box>

                {/* Price */}
                <Box textAlign="center" mt="auto">
                  <Text fontSize="md" fontWeight="bold" color="gray.700">
                    Ціна: {product.price} грн
                  </Text>
                </Box>
              </VStack>
            </GridItem>
          );
        })}
      </Grid>

      {loading && <Spinner mt={4} />}
    </Box>
  );
};

export default ProductCardPrinter;